import deepmerge from 'deepmerge';
import React, { Fragment, useMemo } from 'react';

import {
  ComponentName,
  ResponsiveCssControlsDescription,
  Section,
  SectionContent,
} from 'craft/containers/Toolbox/components';
import { CollectionFieldEditor } from 'craft/containers/Toolbox/components/CollectionFieldEditor';
import {
  Background,
  Border,
  GenericProperties,
  Layout,
  NameAndType,
  SizeToolboxSection,
  Spacing,
} from 'craft/containers/Toolbox/sections';
import { Shadow } from 'craft/containers/Toolbox/sections/Shadow';
import { useMultiNodes, useResolver, useSelection } from 'craft/hooks';
import { FlowToolbox } from 'craft/resolvers/Flow';
import { FormToolbox } from 'craft/resolvers/Forms/toolbox';
import { CopyText } from 'craft/resolvers/RichText/toolbox/CopyText';
import { useIsUsingDefaultCssControls } from 'craft/state';
import { defaultToolboxComponentSchema, ToolboxComponentSchema, ToolboxComponentSections } from 'craft/types';

interface Props {
  nodeIds: string[];
}

export const Component = ({ nodeIds }: Props) => {
  const isUsingDefaultCssControls = useIsUsingDefaultCssControls();
  const { type, parentId } = useMultiNodes(nodeIds, (node) => ({ parentId: node?.data.parent }));
  const { selection } = useSelection();
  const resolver = useResolver(type);

  const toolboxOptions = resolver?.toolbox || {};
  const toolboxSchema = useMemo(() => {
    return deepmerge(
      defaultToolboxComponentSchema,
      toolboxOptions.schema || {},
    ) as ToolboxComponentSchema;
  }, [toolboxOptions.schema]);

  // Find generic props
  const hasActiveEditableAncestor = selection.all.some(
    (nodeId: string) => !!document.querySelector(`[data-rte-node=true][data-nid^="${nodeId}"]`),
  );

  if (hasActiveEditableAncestor) {
    if (!isUsingDefaultCssControls) {
      return null;
    }
    return (
      <>
        <SectionContent>
          <ComponentName nodeId={nodeIds[0]} />
        </SectionContent>
        <CopyText nodeIds={nodeIds} />
      </>
    );
  }

  if (toolboxOptions.hasOnlyFlowToolbox) {
    if (!isUsingDefaultCssControls) {
      return null;
    }
    return (
      <>
        <SectionContent>
          <ComponentName prefix="Step" nodeId={nodeIds[0]} />
        </SectionContent>
        <FlowToolbox nodeIds={nodeIds} />
      </>
    );
  }

  const nodeKeys = nodeIds.toString().replace(/,/g, '-');

  const customComponent = toolboxOptions.component
    ?.filter((CustomSection) => CustomSection)
    .map((CustomSection, i) => (
      <CustomSection
        key={`customComponent-${nodeKeys}-${i.toString()}`}
        parentId={parentId || undefined}
        nodeIds={nodeIds}
      />
    ));

  const customResponsiveComponent = toolboxOptions.responsiveComponent?.map((CustomSection, i) => (
    <CustomSection
      key={`customResponsiveComponent-${nodeKeys}-${i.toString()}`}
      parentId={parentId || undefined}
      nodeIds={nodeIds}
    />
  ));

  const customStyles = (isUsingDefaultCssControls
    ? toolboxOptions.styles
    : // @ts-expect-error
      (toolboxOptions.styles || []).filter((StyleSection) => StyleSection.craft?.isResponsiveStyle)
  )?.map((CustomSection, i) => (
    <CustomSection
      key={`customStyles-${nodeKeys}-${i.toString()}`}
      parentId={parentId || undefined}
      nodeIds={nodeIds}
    />
  ));

  const customResponsiveStyles = toolboxOptions.responsiveStyles?.map((CustomSection, i) => (
    <CustomSection
      key={`customResponsiveStyles-${nodeKeys}-${i.toString()}`}
      parentId={parentId || undefined}
      nodeIds={nodeIds}
    />
  ));

  return (
    <Fragment>
      {!isUsingDefaultCssControls && <ResponsiveCssControlsDescription key={nodeKeys} />}

      {isUsingDefaultCssControls && (
        <NameAndType
          key={`name-${nodeKeys}`}
          nodeIds={nodeIds}
          schema={toolboxSchema[ToolboxComponentSections.NameAndType]}
        />
      )}

      {/** Form menus */}
      {isUsingDefaultCssControls && <FormToolbox key={`form-${nodeKeys}`} nodeIds={nodeIds} />}

      {isUsingDefaultCssControls && customComponent}

      {!isUsingDefaultCssControls && customResponsiveComponent}

      {/** Collection menus */}
      <CollectionFieldEditor nodeIds={nodeIds} />

      {isUsingDefaultCssControls && toolboxSchema[ToolboxComponentSections.Generic].isVisible && (
        <GenericProperties key={`generic-${nodeKeys}`} nodeIds={nodeIds} />
      )}

      {toolboxSchema[ToolboxComponentSections.Layout].isVisible && (
        <Layout
          key={`layout-${nodeKeys}`}
          nodeIds={nodeIds}
          schema={toolboxSchema[ToolboxComponentSections.Layout]}
        />
      )}

      {toolboxSchema[ToolboxComponentSections.Size].isVisible && (
        <SizeToolboxSection
          key={`size-${nodeKeys}`}
          nodeIds={nodeIds}
          schema={toolboxSchema[ToolboxComponentSections.Size]}
        />
      )}

      {(toolboxSchema[ToolboxComponentSections.Spacing].hasMargin ||
        toolboxSchema[ToolboxComponentSections.Spacing].hasPadding) && (
        <Section name="Spacing" data-cy="toolbox-section-spacing">
          <Spacing
            key={`spacing-${nodeKeys}`}
            nodeIds={nodeIds}
            schema={toolboxSchema[ToolboxComponentSections.Spacing]}
          />
        </Section>
      )}

      {isUsingDefaultCssControls && customStyles}

      {!isUsingDefaultCssControls && customResponsiveStyles}

      {/** Flow menus */}
      {isUsingDefaultCssControls && <FlowToolbox key={`flows-${nodeKeys}`} nodeIds={nodeIds} />}

      {isUsingDefaultCssControls && (
        <>
          <Background
            key={`background-${nodeKeys}`}
            nodeIds={nodeIds}
            schema={toolboxSchema[ToolboxComponentSections.Background]}
          />
          {toolboxSchema[ToolboxComponentSections.Border].isVisible && (
            <Border
              key={`border-${nodeKeys}`}
              nodeIds={nodeIds}
              schema={toolboxSchema[ToolboxComponentSections.Border]}
              hasOverflow={toolboxSchema[ToolboxComponentSections.Size].hasOverflow}
            />
          )}
          {toolboxSchema[ToolboxComponentSections.Shadow].isVisible && (
            <Shadow
              key={`shadow-${nodeKeys}`}
              nodeIds={nodeIds}
              schema={toolboxSchema[ToolboxComponentSections.Shadow]}
            />
          )}
        </>
      )}
    </Fragment>
  );
};
